import { API, Endpoints } from '@brg/api'
import { LmsCustomVoteGroup } from './testItem'

export type LocalizedName = { lang: string; name: string }

export type ExplainMode = 'never' | 'after_submit' | 'if_passed' | 'after_each'

export type ManagerTestItemType = 0 | 1 | 2

export const MANAGER_TEST_ITEM_TYPE_TEST: ManagerTestItemType = 0
export const MANAGER_TEST_ITEM_TYPE_QUESTIONNAIRE: ManagerTestItemType = 1
export const MANAGER_TEST_ITEM_TYPE_SOP: ManagerTestItemType = 2

export type LmsVoteFormatType = 0 | 1 | 2
export const LMS_VOTE_FORMAT_TEACHER: LmsVoteFormatType = 0
export const LMS_VOTE_FORMAT_COURSE: LmsVoteFormatType = 1
export const LMS_VOTE_FORMAT_TEACHER_COURSE: LmsVoteFormatType = 2

export type LmsVoteType = 0 | 1 | 2
export const LMS_VOTE_TYPE_ALL: LmsVoteType = 0
export const LMS_VOTE_TYPE_GROUP: LmsVoteType = 1
export const LMS_VOTE_TYPE_CUSTOM: LmsVoteType = 2

export type ManagerTestItemAddPayload = {
  entityId: string
  entityType: 'longread'
  title: LocalizedName[]
  description: LocalizedName[]
  bgColor: string
  passingScore: number | null
  numberOfAttempts: number | null
  timeToComplete: string | null
  accessType: number
  shuffleQuestions: boolean
  shuffleAnswers: boolean
  showCorrectAnswers: boolean
  showExplanationsType: number
  status: number
  type: ManagerTestItemType
  startDate?: string | null
  endDate?: string | null
  lmsModuleId?: string | null
  lmsModuleIds?: string[] | null
  lmsVoteFormatType?: LmsVoteFormatType | null
  lmsVoteType?: LmsVoteType | null
  lmsVoteGroupIds?: string[] | null
  lmsCustomVoteGroups?: LmsCustomVoteGroup[] | null
}

function parseNonNegativeInt(s: string): number {
  const n = parseInt(String(s).trim(), 10)
  if (!Number.isFinite(n) || n < 0) return 0
  return n
}

function parseOptionalPassingScorePercent(s: string): number {
  const t = String(s).trim()
  if (t === '') {
    return 0
  }
  const n = parseInt(t, 10)
  if (!Number.isFinite(n) || n < 0) {
    return 0
  }
  return Math.min(100, n)
}

function parseAttemptsLimitMinOne(s: string): number {
  const t = String(s).trim()
  if (t === '') {
    return 1
  }
  const n = parseInt(t, 10)
  if (!Number.isFinite(n) || n < 1) {
    return 1
  }
  return n
}

function minutesToTimeToComplete(totalMinutes: number): string {
  const m = Math.max(0, Math.floor(totalMinutes))
  const days = Math.floor(m / (24 * 60))
  const rem = m % (24 * 60)
  const h = Math.floor(rem / 60)
  const min = rem % 60
  const hh = String(h).padStart(2, '0')
  const mm = String(min).padStart(2, '0')
  if (days > 0) {
    return `${days}.${hh}:${mm}:00`
  }
  return `${hh}:${mm}:00`
}

function mapAccessType(access: string): number {
  if (access === 'open' || access === '') return 0
  return 1
}

function mapExplainMode(mode: ExplainMode): number {
  switch (mode) {
    case 'never':
      return 0
    case 'after_submit':
      return 1
    case 'if_passed':
      return 2
    case 'after_each':
      return 3
    default:
      return 1
  }
}

export type BuildAddTestPayloadInput = {
  entityId: string
  title: { ru: string; en: string }
  description: { ru: string; en: string }
  headerBackgroundColor: string
  passScore: string
  durationMin: string
  attemptsLimit: string
  access: string
  shuffleQuestions: boolean
  shuffleAnswers: boolean
  showResultsAfterAnswer: boolean
  explainMode: ExplainMode
}

export function buildManagerTestItemAddPayload(
  input: BuildAddTestPayloadInput
): ManagerTestItemAddPayload {
  const minutes = parseNonNegativeInt(input.durationMin)
  const color =
    String(input.headerBackgroundColor ?? '').trim() || '#ffffff'
  return {
    entityId: input.entityId,
    entityType: 'longread',
    title: [
      { lang: 'ru', name: input.title.ru },
      { lang: 'en', name: input.title.en }
    ],
    description: [
      { lang: 'ru', name: input.description.ru },
      { lang: 'en', name: input.description.en }
    ],
    bgColor: color,
    passingScore: parseOptionalPassingScorePercent(input.passScore),
    numberOfAttempts: parseAttemptsLimitMinOne(input.attemptsLimit),
    timeToComplete: minutesToTimeToComplete(minutes),
    accessType: mapAccessType(input.access),
    shuffleQuestions: input.shuffleQuestions,
    shuffleAnswers: input.shuffleAnswers,
    showCorrectAnswers: input.showResultsAfterAnswer,
    showExplanationsType: mapExplainMode(input.explainMode),
    status: 0,
    type: MANAGER_TEST_ITEM_TYPE_TEST
  }
}

export function buildManagerQuestionnaireItemPayload(input: {
  entityId: string
  title: { ru: string; en: string }
  description: { ru: string; en: string }
  headerBackgroundColor: string
}): ManagerTestItemAddPayload {
  return {
    ...buildManagerTestItemAddPayload({
      entityId: input.entityId,
      title: input.title,
      description: input.description,
      headerBackgroundColor: input.headerBackgroundColor,
      passScore: '0',
      durationMin: '0',
      attemptsLimit: '1',
      access: 'open',
      shuffleQuestions: false,
      shuffleAnswers: false,
      showResultsAfterAnswer: false,
      explainMode: 'never'
    }),
    passingScore: null,
    numberOfAttempts: null,
    timeToComplete: null,
    type: MANAGER_TEST_ITEM_TYPE_QUESTIONNAIRE
  }
}

export type BuildSopPayloadInput = {
  entityId: string
  name: string
  startDate?: string | null
  endDate?: string | null
  lmsModuleId: string
  lmsModuleIds?: string[]
  lmsVoteFormatType: LmsVoteFormatType
  lmsVoteType: LmsVoteType
  lmsVoteGroupIds?: string[]
  lmsCustomVoteGroups?: LmsCustomVoteGroup[]
  status?: number
}

export function buildManagerSopItemPayload(
  input: BuildSopPayloadInput
): ManagerTestItemAddPayload {
  const name = String(input.name ?? '').trim()
  return {
    ...buildManagerQuestionnaireItemPayload({
      entityId: input.entityId,
      title: { ru: name, en: name },
      description: { ru: '', en: '' },
      headerBackgroundColor: '#ffffff',
    }),
    type: MANAGER_TEST_ITEM_TYPE_SOP,
    startDate: input.startDate?.trim() ? input.startDate : null,
    endDate: input.endDate?.trim() ? input.endDate : null,
    lmsModuleId: input.lmsModuleId || input.lmsModuleIds?.[0] || null,
    lmsModuleIds: input.lmsModuleIds?.length
      ? input.lmsModuleIds
      : input.lmsModuleId
        ? [input.lmsModuleId]
        : [],
    lmsVoteFormatType: input.lmsVoteFormatType,
    lmsVoteType: input.lmsVoteType,
    lmsVoteGroupIds: input.lmsVoteGroupIds?.length ? input.lmsVoteGroupIds : [],
    lmsCustomVoteGroups: input.lmsCustomVoteGroups?.length
      ? input.lmsCustomVoteGroups
      : [],
    status: input.status ?? 0,
  }
}

export type AddManagerTestItemOptions = {
  accessToken?: string | null
}

export function parseManagerTestItemAddResponse(data: unknown): string | null {
  if (data === null || typeof data !== 'object') return null
  const o = data as Record<string, unknown>
  if (typeof o.testItemId === 'string' && o.testItemId.trim()) {
    return o.testItemId.trim()
  }
  if (typeof o.id === 'string' && o.id.trim()) {
    return o.id.trim()
  }
  const dataField = o.data
  if (dataField !== null && typeof dataField === 'object') {
    const d = dataField as Record<string, unknown>
    if (typeof d.testItemId === 'string' && d.testItemId.trim()) {
      return d.testItemId.trim()
    }
    if (typeof d.id === 'string' && d.id.trim()) {
      return d.id.trim()
    }
  }
  return null
}

function formatManagerTestItemHttpError(status: number, bodyText: string): string {
  let msg = `HTTP ${status}`
  if (!bodyText.trim()) {
    return msg
  }
  try {
    const errBody = JSON.parse(bodyText) as Record<string, unknown>
    const title = errBody.title
    const message = errBody.message ?? errBody.error ?? errBody.detail
    const errors = errBody.errors
    if (typeof title === 'string') {
      msg = title
    } else if (typeof message === 'string') {
      msg = message
    }
    if (errors !== null && typeof errors === 'object' && !Array.isArray(errors)) {
      const parts = Object.entries(errors as Record<string, unknown>).map(
        ([k, v]) =>
          `${k}: ${Array.isArray(v) ? v.join(', ') : String(v)}`
      )
      if (parts.length > 0) {
        msg = msg ? `${msg} — ${parts.join('; ')}` : parts.join('; ')
      }
    }
  } catch {
  }
  return msg
}

export const MANAGER_TEST_ITEM_GET_LIST_URL =
  'testsservice/api/ManagerTestItem/GetList'

export type FetchManagerTestItemsListOptions = {
  accessToken?: string | null
  statuses?: number[]
  courseId?: string
}

export async function fetchManagerTestItemsList(
  options?: FetchManagerTestItemsListOptions
): Promise<unknown> {
  let req = API.request()
    .endpoint(Endpoints.gateway)
    .url(MANAGER_TEST_ITEM_GET_LIST_URL)
    .get()

  const courseId = options?.courseId?.trim()
  if (courseId) {
    req = req.addQuery('CourseId', courseId)
  }

  const statuses = options?.statuses
  if (Array.isArray(statuses) && statuses.length > 0) {
    req = req.addQuery('Statuses', statuses)
  }

  const token = options?.accessToken?.trim()
  if (token) {
    req = req.headers({ Authorization: `Bearer ${token}` })
  }

  const response = await req.send()
  const text = await response.text()

  if (!response.ok) {
    throw new Error(formatManagerTestItemHttpError(response.status, text))
  }

  if (!text.trim()) {
    return []
  }

  try {
    return JSON.parse(text) as unknown
  } catch {
    throw new Error('Invalid JSON response')
  }
}

export const MANAGER_TEST_ITEM_HAS_TESTS_URL =
  'testsservice/api/ManagerTestItem/HasTests'

export async function fetchManagerTestItemHasTests(
  options?: AddManagerTestItemOptions
): Promise<unknown> {
  let req = API.request()
    .endpoint(Endpoints.gateway)
    .url(MANAGER_TEST_ITEM_HAS_TESTS_URL)
    .get()

  const token = options?.accessToken?.trim()
  if (token) {
    req = req.headers({ Authorization: `Bearer ${token}` })
  }

  const response = await req.send()
  const text = await response.text()

  if (!response.ok) {
    throw new Error(formatManagerTestItemHttpError(response.status, text))
  }

  if (!text.trim()) {
    return null
  }

  try {
    return JSON.parse(text) as unknown
  } catch {
    throw new Error('Invalid JSON response')
  }
}

function coerceHasTestsPrimitive(value: unknown): boolean | null {
  if (typeof value === 'boolean') {
    return value
  }
  if (typeof value === 'number') {
    if (value === 1) {
      return true
    }
    if (value === 0) {
      return false
    }
  }
  if (typeof value === 'string') {
    const s = value.trim().toLowerCase()
    if (s === 'true' || s === '1') {
      return true
    }
    if (s === 'false' || s === '0') {
      return false
    }
  }
  return null
}

export function parseManagerTestItemHasTests(data: unknown): boolean | null {
  if (data === null || data === undefined) {
    return null
  }
  const direct = coerceHasTestsPrimitive(data)
  if (direct !== null) {
    return direct
  }
  if (typeof data !== 'object') {
    return null
  }
  const o = data as Record<string, unknown>
  const keys = ['hasTests', 'HasTests', 'has_test', 'value', 'Value']
  for (const k of keys) {
    if (!(k in o)) {
      continue
    }
    const inner = coerceHasTestsPrimitive(o[k])
    if (inner !== null) {
      return inner
    }
  }
  const nested = o.data ?? o.Data ?? o.result ?? o.Result
  if (nested !== undefined && nested !== null && nested !== data) {
    return parseManagerTestItemHasTests(nested)
  }
  return null
}

export async function fetchManagerTestItem(
  testItemId: string,
  options?: AddManagerTestItemOptions
): Promise<unknown> {
  const id = String(testItemId ?? '').trim()
  if (!id) {
    throw new Error('testItemId required')
  }

  let req = API.request()
    .endpoint(Endpoints.gateway)
    .url('testsservice/api/ManagerTestItem/Get')
    .addQuery('id', id)
    .get()

  const token = options?.accessToken?.trim()
  if (token) {
    req = req.headers({ Authorization: `Bearer ${token}` })
  }

  const response = await req.send()
  const text = await response.text()

  if (!response.ok) {
    throw new Error(formatManagerTestItemHttpError(response.status, text))
  }

  if (!text.trim()) {
    return {}
  }

  try {
    return JSON.parse(text) as unknown
  } catch {
    throw new Error('Invalid JSON response')
  }
}

export async function addManagerTestItem(
  payload: ManagerTestItemAddPayload,
  options?: AddManagerTestItemOptions
): Promise<unknown> {
  let req = API.request()
    .endpoint(Endpoints.gateway)
    .url('testsservice/api/ManagerTestItem/Add')
    .post()
    .body(payload)

  const token = options?.accessToken?.trim()
  if (token) {
    req = req.headers({ Authorization: `Bearer ${token}` })
  }

  const response = await req.send()
  const text = await response.text()

  if (!response.ok) {
    throw new Error(formatManagerTestItemHttpError(response.status, text))
  }

  if (!text.trim()) {
    return {}
  }

  try {
    return JSON.parse(text) as unknown
  } catch {
    throw new Error('Invalid JSON response')
  }
}

export type ManagerTestItemUpdatePayload = ManagerTestItemAddPayload & { id: string }

export async function updateManagerTestItem(
  payload: ManagerTestItemUpdatePayload,
  options?: AddManagerTestItemOptions
): Promise<unknown> {
  let req = API.request()
    .endpoint(Endpoints.gateway)
    .url('testsservice/api/ManagerTestItem/Update')
    .post()
    .body(payload)

  const token = options?.accessToken?.trim()
  if (token) {
    req = req.headers({ Authorization: `Bearer ${token}` })
  }

  const response = await req.send()
  const text = await response.text()

  if (!response.ok) {
    throw new Error(formatManagerTestItemHttpError(response.status, text))
  }

  if (!text.trim()) {
    return {}
  }

  try {
    return JSON.parse(text) as unknown
  } catch {
    throw new Error('Invalid JSON response')
  }
}

export async function updateManagerTestItemStatus(
  params: { id: string; status: number },
  options?: AddManagerTestItemOptions
): Promise<unknown> {
  const id = String(params.id ?? '').trim()
  if (!id) {
    throw new Error('test id required')
  }
  if (typeof params.status !== 'number' || !Number.isFinite(params.status)) {
    throw new Error('status required')
  }

  let req = API.request()
    .endpoint(Endpoints.gateway)
    .url('testsservice/api/ManagerTestItem/UpdateStatus')
    .body({ id, status: params.status })
    .post()

  const token = options?.accessToken?.trim()
  if (token) {
    req = req.headers({ Authorization: `Bearer ${token}` })
  }

  const response = await req.send()
  const text = await response.text()

  if (!response.ok) {
    throw new Error(formatManagerTestItemHttpError(response.status, text))
  }

  if (!text.trim()) {
    return {}
  }

  try {
    return JSON.parse(text) as unknown
  } catch {
    throw new Error('Invalid JSON response')
  }
}
import { API, Endpoints } from '@brg/api'
import { LmsCustomVoteGroup } from './testItem'

export type LocalizedName = { lang: string; name: string }

export type ExplainMode = 'never' | 'after_submit' | 'if_passed' | 'after_each'

export type ManagerTestItemType = 0 | 1 | 2

export const MANAGER_TEST_ITEM_TYPE_TEST: ManagerTestItemType = 0
export const MANAGER_TEST_ITEM_TYPE_QUESTIONNAIRE: ManagerTestItemType = 1
export const MANAGER_TEST_ITEM_TYPE_SOP: ManagerTestItemType = 2

export type LmsVoteFormatType = 0 | 1 | 2
export const LMS_VOTE_FORMAT_TEACHER: LmsVoteFormatType = 0
export const LMS_VOTE_FORMAT_COURSE: LmsVoteFormatType = 1
export const LMS_VOTE_FORMAT_TEACHER_COURSE: LmsVoteFormatType = 2

export type LmsVoteType = 0 | 1 | 2
export const LMS_VOTE_TYPE_ALL: LmsVoteType = 0
export const LMS_VOTE_TYPE_GROUP: LmsVoteType = 1
export const LMS_VOTE_TYPE_CUSTOM: LmsVoteType = 2

export type ManagerTestItemAddPayload = {
  entityId: string
  entityType: 'longread'
  title: LocalizedName[]
  description: LocalizedName[]
  bgColor: string
  passingScore: number | null
  numberOfAttempts: number | null
  timeToComplete: string | null
  accessType: number
  shuffleQuestions: boolean
  shuffleAnswers: boolean
  showCorrectAnswers: boolean
  showExplanationsType: number
  status: number
  type: ManagerTestItemType
  startDate?: string | null
  endDate?: string | null
  lmsModuleId?: string | null
  lmsModuleIds?: string[] | null
  lmsVoteFormatType?: LmsVoteFormatType | null
  lmsVoteType?: LmsVoteType | null
  lmsVoteGroupIds?: string[] | null
  lmsCustomVoteGroups?: LmsCustomVoteGroup[] | null
}

function parseNonNegativeInt(s: string): number {
  const n = parseInt(String(s).trim(), 10)
  if (!Number.isFinite(n) || n < 0) return 0
  return n
}

function parseOptionalPassingScorePercent(s: string): number {
  const t = String(s).trim()
  if (t === '') {
    return 0
  }
  const n = parseInt(t, 10)
  if (!Number.isFinite(n) || n < 0) {
    return 0
  }
  return Math.min(100, n)
}

function parseAttemptsLimitMinOne(s: string): number {
  const t = String(s).trim()
  if (t === '') {
    return 1
  }
  const n = parseInt(t, 10)
  if (!Number.isFinite(n) || n < 1) {
    return 1
  }
  return n
}

function minutesToTimeToComplete(totalMinutes: number): string {
  const m = Math.max(0, Math.floor(totalMinutes))
  const days = Math.floor(m / (24 * 60))
  const rem = m % (24 * 60)
  const h = Math.floor(rem / 60)
  const min = rem % 60
  const hh = String(h).padStart(2, '0')
  const mm = String(min).padStart(2, '0')
  if (days > 0) {
    return `${days}.${hh}:${mm}:00`
  }
  return `${hh}:${mm}:00`
}

function mapAccessType(access: string): number {
  if (access === 'open' || access === '') return 0
  return 1
}

function mapExplainMode(mode: ExplainMode): number {
  switch (mode) {
    case 'never':
      return 0
    case 'after_submit':
      return 1
    case 'if_passed':
      return 2
    case 'after_each':
      return 3
    default:
      return 1
  }
}

export type BuildAddTestPayloadInput = {
  entityId: string
  title: { ru: string; en: string }
  description: { ru: string; en: string }
  headerBackgroundColor: string
  passScore: string
  durationMin: string
  attemptsLimit: string
  access: string
  shuffleQuestions: boolean
  shuffleAnswers: boolean
  showResultsAfterAnswer: boolean
  explainMode: ExplainMode
}

export function buildManagerTestItemAddPayload(
  input: BuildAddTestPayloadInput
): ManagerTestItemAddPayload {
  const minutes = parseNonNegativeInt(input.durationMin)
  const color =
    String(input.headerBackgroundColor ?? '').trim() || '#ffffff'
  return {
    entityId: input.entityId,
    entityType: 'longread',
    title: [
      { lang: 'ru', name: input.title.ru },
      { lang: 'en', name: input.title.en }
    ],
    description: [
      { lang: 'ru', name: input.description.ru },
      { lang: 'en', name: input.description.en }
    ],
    bgColor: color,
    passingScore: parseOptionalPassingScorePercent(input.passScore),
    numberOfAttempts: parseAttemptsLimitMinOne(input.attemptsLimit),
    timeToComplete: minutesToTimeToComplete(minutes),
    accessType: mapAccessType(input.access),
    shuffleQuestions: input.shuffleQuestions,
    shuffleAnswers: input.shuffleAnswers,
    showCorrectAnswers: input.showResultsAfterAnswer,
    showExplanationsType: mapExplainMode(input.explainMode),
    status: 0,
    type: MANAGER_TEST_ITEM_TYPE_TEST
  }
}

export function buildManagerQuestionnaireItemPayload(input: {
  entityId: string
  title: { ru: string; en: string }
  description: { ru: string; en: string }
  headerBackgroundColor: string
}): ManagerTestItemAddPayload {
  return {
    ...buildManagerTestItemAddPayload({
      entityId: input.entityId,
      title: input.title,
      description: input.description,
      headerBackgroundColor: input.headerBackgroundColor,
      passScore: '0',
      durationMin: '0',
      attemptsLimit: '1',
      access: 'open',
      shuffleQuestions: false,
      shuffleAnswers: false,
      showResultsAfterAnswer: false,
      explainMode: 'never'
    }),
    passingScore: null,
    numberOfAttempts: null,
    timeToComplete: null,
    type: MANAGER_TEST_ITEM_TYPE_QUESTIONNAIRE
  }
}

export type BuildSopPayloadInput = {
  entityId: string
  name: string
  startDate?: string | null
  endDate?: string | null
  lmsModuleId: string
  lmsModuleIds?: string[]
  lmsVoteFormatType: LmsVoteFormatType
  lmsVoteType: LmsVoteType
  lmsVoteGroupIds?: string[]
  lmsCustomVoteGroups?: LmsCustomVoteGroup[]
  status?: number
}

export function buildManagerSopItemPayload(
  input: BuildSopPayloadInput
): ManagerTestItemAddPayload {
  const name = String(input.name ?? '').trim()
  return {
    ...buildManagerQuestionnaireItemPayload({
      entityId: input.entityId,
      title: { ru: name, en: name },
      description: { ru: '', en: '' },
      headerBackgroundColor: '#ffffff',
    }),
    type: MANAGER_TEST_ITEM_TYPE_SOP,
    startDate: input.startDate?.trim() ? input.startDate : null,
    endDate: input.endDate?.trim() ? input.endDate : null,
    lmsModuleId: input.lmsModuleId || input.lmsModuleIds?.[0] || null,
    lmsModuleIds: input.lmsModuleIds?.length
      ? input.lmsModuleIds
      : input.lmsModuleId
        ? [input.lmsModuleId]
        : [],
    lmsVoteFormatType: input.lmsVoteFormatType,
    lmsVoteType: input.lmsVoteType,
    lmsVoteGroupIds: input.lmsVoteGroupIds?.length ? input.lmsVoteGroupIds : [],
    lmsCustomVoteGroups: input.lmsCustomVoteGroups?.length
      ? input.lmsCustomVoteGroups
      : [],
    status: input.status ?? 0,
  }
}

export type AddManagerTestItemOptions = {
  accessToken?: string | null
}

export function parseManagerTestItemAddResponse(data: unknown): string | null {
  if (data === null || typeof data !== 'object') return null
  const o = data as Record<string, unknown>
  if (typeof o.testItemId === 'string' && o.testItemId.trim()) {
    return o.testItemId.trim()
  }
  if (typeof o.id === 'string' && o.id.trim()) {
    return o.id.trim()
  }
  const dataField = o.data
  if (dataField !== null && typeof dataField === 'object') {
    const d = dataField as Record<string, unknown>
    if (typeof d.testItemId === 'string' && d.testItemId.trim()) {
      return d.testItemId.trim()
    }
    if (typeof d.id === 'string' && d.id.trim()) {
      return d.id.trim()
    }
  }
  return null
}

function formatManagerTestItemHttpError(status: number, bodyText: string): string {
  let msg = `HTTP ${status}`
  if (!bodyText.trim()) {
    return msg
  }
  try {
    const errBody = JSON.parse(bodyText) as Record<string, unknown>
    const title = errBody.title
    const message = errBody.message ?? errBody.error ?? errBody.detail
    const errors = errBody.errors
    if (typeof title === 'string') {
      msg = title
    } else if (typeof message === 'string') {
      msg = message
    }
    if (errors !== null && typeof errors === 'object' && !Array.isArray(errors)) {
      const parts = Object.entries(errors as Record<string, unknown>).map(
        ([k, v]) =>
          `${k}: ${Array.isArray(v) ? v.join(', ') : String(v)}`
      )
      if (parts.length > 0) {
        msg = msg ? `${msg} — ${parts.join('; ')}` : parts.join('; ')
      }
    }
  } catch {
  }
  return msg
}

export const MANAGER_TEST_ITEM_GET_LIST_URL =
  'testsservice/api/ManagerTestItem/GetList'

export type FetchManagerTestItemsListOptions = {
  accessToken?: string | null
  statuses?: number[]
  courseId?: string
}

export async function fetchManagerTestItemsList(
  options?: FetchManagerTestItemsListOptions
): Promise<unknown> {
  let req = API.request()
    .endpoint(Endpoints.gateway)
    .url(MANAGER_TEST_ITEM_GET_LIST_URL)
    .get()

  const courseId = options?.courseId?.trim()
  if (courseId) {
    req = req.addQuery('CourseId', courseId)
  }

  const statuses = options?.statuses
  if (Array.isArray(statuses) && statuses.length > 0) {
    req = req.addQuery('Statuses', statuses)
  }

  const token = options?.accessToken?.trim()
  if (token) {
    req = req.headers({ Authorization: `Bearer ${token}` })
  }

  const response = await req.send()
  const text = await response.text()

  if (!response.ok) {
    throw new Error(formatManagerTestItemHttpError(response.status, text))
  }

  if (!text.trim()) {
    return []
  }

  try {
    return JSON.parse(text) as unknown
  } catch {
    throw new Error('Invalid JSON response')
  }
}

export const MANAGER_TEST_ITEM_HAS_TESTS_URL =
  'testsservice/api/ManagerTestItem/HasTests'

export async function fetchManagerTestItemHasTests(
  options?: AddManagerTestItemOptions
): Promise<unknown> {
  let req = API.request()
    .endpoint(Endpoints.gateway)
    .url(MANAGER_TEST_ITEM_HAS_TESTS_URL)
    .get()

  const token = options?.accessToken?.trim()
  if (token) {
    req = req.headers({ Authorization: `Bearer ${token}` })
  }

  const response = await req.send()
  const text = await response.text()

  if (!response.ok) {
    throw new Error(formatManagerTestItemHttpError(response.status, text))
  }

  if (!text.trim()) {
    return null
  }

  try {
    return JSON.parse(text) as unknown
  } catch {
    throw new Error('Invalid JSON response')
  }
}

function coerceHasTestsPrimitive(value: unknown): boolean | null {
  if (typeof value === 'boolean') {
    return value
  }
  if (typeof value === 'number') {
    if (value === 1) {
      return true
    }
    if (value === 0) {
      return false
    }
  }
  if (typeof value === 'string') {
    const s = value.trim().toLowerCase()
    if (s === 'true' || s === '1') {
      return true
    }
    if (s === 'false' || s === '0') {
      return false
    }
  }
  return null
}

export function parseManagerTestItemHasTests(data: unknown): boolean | null {
  if (data === null || data === undefined) {
    return null
  }
  const direct = coerceHasTestsPrimitive(data)
  if (direct !== null) {
    return direct
  }
  if (typeof data !== 'object') {
    return null
  }
  const o = data as Record<string, unknown>
  const keys = ['hasTests', 'HasTests', 'has_test', 'value', 'Value']
  for (const k of keys) {
    if (!(k in o)) {
      continue
    }
    const inner = coerceHasTestsPrimitive(o[k])
    if (inner !== null) {
      return inner
    }
  }
  const nested = o.data ?? o.Data ?? o.result ?? o.Result
  if (nested !== undefined && nested !== null && nested !== data) {
    return parseManagerTestItemHasTests(nested)
  }
  return null
}

export async function fetchManagerTestItem(
  testItemId: string,
  options?: AddManagerTestItemOptions
): Promise<unknown> {
  const id = String(testItemId ?? '').trim()
  if (!id) {
    throw new Error('testItemId required')
  }

  let req = API.request()
    .endpoint(Endpoints.gateway)
    .url('testsservice/api/ManagerTestItem/Get')
    .addQuery('id', id)
    .get()

  const token = options?.accessToken?.trim()
  if (token) {
    req = req.headers({ Authorization: `Bearer ${token}` })
  }

  const response = await req.send()
  const text = await response.text()

  if (!response.ok) {
    throw new Error(formatManagerTestItemHttpError(response.status, text))
  }

  if (!text.trim()) {
    return {}
  }

  try {
    return JSON.parse(text) as unknown
  } catch {
    throw new Error('Invalid JSON response')
  }
}

export async function addManagerTestItem(
  payload: ManagerTestItemAddPayload,
  options?: AddManagerTestItemOptions
): Promise<unknown> {
  let req = API.request()
    .endpoint(Endpoints.gateway)
    .url('testsservice/api/ManagerTestItem/Add')
    .post()
    .body(payload)

  const token = options?.accessToken?.trim()
  if (token) {
    req = req.headers({ Authorization: `Bearer ${token}` })
  }

  const response = await req.send()
  const text = await response.text()

  if (!response.ok) {
    throw new Error(formatManagerTestItemHttpError(response.status, text))
  }

  if (!text.trim()) {
    return {}
  }

  try {
    return JSON.parse(text) as unknown
  } catch {
    throw new Error('Invalid JSON response')
  }
}

export type ManagerTestItemUpdatePayload = ManagerTestItemAddPayload & { id: string }

export async function updateManagerTestItem(
  payload: ManagerTestItemUpdatePayload,
  options?: AddManagerTestItemOptions
): Promise<unknown> {
  let req = API.request()
    .endpoint(Endpoints.gateway)
    .url('testsservice/api/ManagerTestItem/Update')
    .post()
    .body(payload)

  const token = options?.accessToken?.trim()
  if (token) {
    req = req.headers({ Authorization: `Bearer ${token}` })
  }

  const response = await req.send()
  const text = await response.text()

  if (!response.ok) {
    throw new Error(formatManagerTestItemHttpError(response.status, text))
  }

  if (!text.trim()) {
    return {}
  }

  try {
    return JSON.parse(text) as unknown
  } catch {
    throw new Error('Invalid JSON response')
  }
}

export async function updateManagerTestItemStatus(
  params: { id: string; status: number },
  options?: AddManagerTestItemOptions
): Promise<unknown> {
  const id = String(params.id ?? '').trim()
  if (!id) {
    throw new Error('test id required')
  }
  if (typeof params.status !== 'number' || !Number.isFinite(params.status)) {
    throw new Error('status required')
  }

  let req = API.request()
    .endpoint(Endpoints.gateway)
    .url('testsservice/api/ManagerTestItem/UpdateStatus')
    .body({ id, status: params.status })
    .post()

  const token = options?.accessToken?.trim()
  if (token) {
    req = req.headers({ Authorization: `Bearer ${token}` })
  }

  const response = await req.send()
  const text = await response.text()

  if (!response.ok) {
    throw new Error(formatManagerTestItemHttpError(response.status, text))
  }

  if (!text.trim()) {
    return {}
  }

  try {
    return JSON.parse(text) as unknown
  } catch {
    throw new Error('Invalid JSON response')
  }
}
